/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback, useRef } from 'preact/hooks';
import { useActive } from '../hooks/UNSAFE_useActive';
import { useFocus } from '../hooks/UNSAFE_useFocus';
import { useTestId } from '../hooks/UNSAFE_useTestId';
import { useHover } from '../hooks/UNSAFE_useHover/useHover';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import type { BaseSelectorProps as Props } from '../UNSAFE_Selector';
import { StyledCheckbox } from '../PRIVATE_StyledCheckbox';
import { styles } from './themes/SelectorStyles.css';
import { useCollectionInteractionContext } from '#hooks/UNSAFE_useCollectionInteractionContext';

export function BaseSelector({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  isChecked,
  onSelectionHandler,
  tooltipContent,
  tooltipProps = {},
  testId
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { isActive, activeProps } = useActive({ disabledKeys: ['enter'] });
  const { isFocus, focusProps } = useFocus();
  const { isHover, hoverProps } = useHover();
  const {
    onKeyUp: onActiveKeyUp,
    onKeyDown: onActiveKeyDown,
    onBlur: onActiveBlur,
    ...activeRest
  } = activeProps;

  const onFocusOut = () => {
    onClickRef.current = false;
  };

  const testIdProps = useTestId(testId);

  /*****************************************************************************************/
  /************************************* Key Event Handler***********************************/
  /*****************************************************************************************/

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      onKeyDown(e);
      onActiveKeyDown(e);
    },
    [onActiveKeyDown]
  );

  // because we stop event propagation on selection, we must ensure
  // that the event gets passed to useActive's onKeyUp handler first
  // so that it doesn't get stuck in "active" mode
  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      onActiveKeyUp(e);

      onSelectionHandler(e);
    },
    [onActiveKeyUp, onSelectionHandler]
  );

  /*****************************************************************************************/
  /************************************* OnCLick *******************************************/
  /*****************************************************************************************/
  const onClickRef = useRef(false);

  const onClick = useCallback(
    (event: MouseEvent) => {
      onClickRef.current = true;
      inputRef.current?.focus();

      onSelectionHandler(event);
    },
    [onSelectionHandler]
  );

  const isEmbedded = useCollectionInteractionContext() === 'embedded';

  // when not 'embedded', prevent focus loss due to mouse interactions to ensure active state
  // works consistently whether clicking directly on or within the hover region of the selector
  const onMouseDown = useCallback(
    (event: MouseEvent) => {
      if (!isEmbedded) {
        event.preventDefault();
      }
    },
    [isEmbedded]
  );

  return (
    <div
      class={styles.base}
      {...testIdProps}
      {...mergeProps(
        hoverProps,
        activeRest,
        {
          onClick,
          onFocusOut,
          onMouseDown
        },
        tooltipProps
      )}>
      <StyledCheckbox
        ref={inputRef}
        isHover={isHover}
        isChecked={isChecked}
        isActive={isActive}
        isEmbedded={isEmbedded}
        isFocusRingShown={isFocus && !onClickRef.current}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        {...mergeProps(focusProps, { onBlur: onActiveBlur })}
      />
      {tooltipContent}
    </div>
  );
}

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === ' ') {
    // prevent auto scrolling and prevent event from bubbling to parent List items
    event.preventDefault();
    event.stopPropagation();
  }
};
